/**
 * Accepts simple type checking functions (like `value => typeof value === 'string'`) or compiled Typebox validators.
 */
export type Validator<T> =
  | ((value: unknown) => value is T)
  // Accepting just validation functions would be simpler and should allow just passing `validator.Check` as the value,
  // but in practice that doesn't work because `Validator#Check` reads the validator's `this` binding, so you need to
  // pass the entire object around together.
  | {Check: (value: unknown) => value is T}

function isValid<T>(value: unknown, validator: Validator<T>): value is T {
  return typeof validator === 'function' ? validator(value) : validator.Check(value)
}

/**
 * If `validateValue` is provided, will return `value` if valid or otherwise return `defaultValue`.
 *
 * If `validateValue` is not provided, will just return `value` cast as `T` (unsafe!).
 */
export function validOrDefault<T>(value: unknown, defaultValue: T, validator?: Validator<T>) {
  if (validator) {
    return isValid(value, validator) ? value : defaultValue
  } else {
    return value as T
  }
}
